import {
  IQuoteActions,
  IQuoteApplySurchargesPayload,
  IQuoteComputationEvaluatePricePayload,
  IQuoteExportXMLActionPayload,
  IQuoteImportActionPayload,
  IQuoteSetBiddingEnvironmentActionPayload,
  IQuoteSetClassificationActionPayload,
  IQuoteSetCustomerRelationshipOwnerActionPayload,
  IQuoteUploadRFQAttachmentActionPayload,
  IQuoteExportPackageDocumentPayload,
  QuoteContext,
  IQuoteApplySurchargeDiscountPercentagePayload
} from './actions.types';
import {
  IQuoteData,
  IQuotePackage,
  IQuotePackageData,
  IQuotePackageDocument,
  IQuotePackageDocumentData
} from '@/models';
import {
  IQuoteConfigurationItemGetActionPayload,
  IQuoteConfigurationItemSetActionPayload,
  IQuoteExportActionPayload,
  IQuoteExportProformaActionPayload,
  IQuoteFetchActionPayload,
  IQuoteOrderActionPayload,
  IQuoteGlobalDiscountActionPayload,
  IQuoteFirstYearDiscountActionPayload,
  IQuoteRevisionActionPayload,
  IQuoteSetCommissionActionPayload,
  IQuoteSetTargetDiscountActionPayload,
  IQuoteSetCommentsActionPayload,
  IQuoteSetRegionActionPayload,
  IQuoteSetStateActionPayload,
  IQuoteSetUserActionPayload,
  IQuoteCalculateCoreDiscountPercentPayload,
  IQuoteApplyDiscountPercentagesPayload,
  IQuoteSetQuotePackageQuotesActionPayload
} from '.';
import { Transition } from '@/models/transition';
import { IConfigurationItemData } from '@/models/configurator/configuration';
import { IQuotePackageParams, IQuotePaginatedListData } from '@/services';
import {
  IAddQuotePackageDocumentMutationPayload,
  ISetQuoteFieldStateMutationPayload
} from '../mutations';

export const actions: IQuoteActions = {
  fetchQuotes(
    context: QuoteContext,
    payload: IQuoteFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.quote.fetchQuotes(payload);
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then((quotes: IQuotePaginatedListData) => {
      if (promise == context.state.lastPromise) {
        context.commit('SET_QUOTES', quotes.results);
        context.commit('SET_COUNT', quotes.count);
        context.commit('SET_LAST_PROMISE', null);
      }
    });
  },

  fetchQuote(context: QuoteContext, payload: number): Promise<void> {
    return this.$services.quote
      .fetchQuote(payload)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  createQuote(context: QuoteContext, payload: IQuoteData): Promise<void> {
    return this.$services.quote
      .createQuote(payload)
      .then((quote: IQuoteData) => {
        context.commit('ADD_QUOTE', quote);
        context.commit('INCREMENT_COUNT');
      });
  },

  updateQuote(context: QuoteContext, payload: IQuoteData): Promise<void> {
    return this.$services.quote
      .updateQuote(payload)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  deleteQuote(context: QuoteContext, payload: number): Promise<void> {
    return this.$services.quote.deleteQuote(payload).then(() => {
      context.commit('REMOVE_QUOTE', payload);
      context.commit('DECREMENT_COUNT');
    });
  },

  // Quote Revisions
  fetchQuoteRevision(
    context: QuoteContext,
    payload: IQuoteRevisionActionPayload
  ): Promise<void> {
    return this.$services.quote
      .fetchQuoteRevision(payload.quoteId, payload.revisionId)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) =>
            v.number === quote.number && v.revision === quote.revision
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  fetchQuoteRevisions(
    _context: QuoteContext,
    payload: number
  ): Promise<IQuoteData[]> {
    return this.$services.quote
      .fetchQuoteRevisions(payload)
      .then((quotes: IQuoteData[]) => {
        return quotes;
      });
  },

  createQuoteRevision(
    context: QuoteContext,
    payload: IQuoteData
  ): Promise<void> {
    return this.$services.quote
      .createQuoteRevision(payload.number!, payload)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  setLatestRevision(
    _context: QuoteContext,
    payload: IQuoteRevisionActionPayload
  ): Promise<IQuoteData> {
    return this.$services.quote.setLatestRevision(
      payload.quoteId,
      payload.revisionId
    );
  },

  exportQuoteItems(
    _context: QuoteContext,
    payload: IQuoteRevisionActionPayload
  ): Promise<IQuoteData> {
    return this.$services.quote.exportQuoteItems(
      payload.quoteId,
      payload.revisionId
    );
  },

  exportQuoteItemsMargins(
    _context: QuoteContext,
    payload: IQuoteRevisionActionPayload
  ): Promise<IQuoteData> {
    return this.$services.quote.exportQuoteItemsMargins(
      payload.quoteId,
      payload.revisionId
    );
  },

  fetchQuoteActivities(
    context: QuoteContext,
    payload: IQuoteRevisionActionPayload
  ): Promise<void> {
    return this.$services.quote
      .fetchQuoteActivities(payload.quoteId, payload.revisionId)
      .then((activities: Transition[]) => {
        context.commit('SET_QUOTE_ACTIVITIES', activities);
      });
  },

  setQuoteState(
    context: QuoteContext,
    payload: IQuoteSetStateActionPayload
  ): Promise<number> {
    return this.$services.quote
      .setQuoteState(payload.id, payload.state)
      .then(({ state }) => {
        context.commit('SET_QUOTE_STATE', { id: payload.id, state: state });
        return state;
      });
  },

  setQuoteOwner(
    context: QuoteContext,
    payload: IQuoteSetUserActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteOwner(payload.id, payload.user)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'owner',
          data: payload.user
        } as ISetQuoteFieldStateMutationPayload<'owner'>);
      });
  },

  downloadRfqAttachment(
    _context: QuoteContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.quote.downloadRfqAttachment(payload);
  },

  uploadRfqAttachment(
    context: QuoteContext,
    payload: IQuoteUploadRFQAttachmentActionPayload
  ): Promise<void> {
    return this.$services.quote
      .uploadRfqAttachment(payload.number, payload.file_upload)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  setQuoteCommissionPercent(
    context: QuoteContext,
    payload: IQuoteSetCommissionActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteCommissionPercent(payload.id, payload.commission_percent)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'commission',
          data: payload.commission_percent
        } as ISetQuoteFieldStateMutationPayload<'commission'>);
      });
  },

  setQuoteTargetDiscountPercent(
    context: QuoteContext,
    payload: IQuoteSetTargetDiscountActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteTargetDiscountPercent(payload.id, payload.percentage)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'target_discount_percent',
          data: payload.percentage
        } as ISetQuoteFieldStateMutationPayload<'target_discount_percent'>);
      });
  },

  setQuoteInsideSalesOwner(
    context: QuoteContext,
    payload: IQuoteSetUserActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteInsideSalesOwner(payload.id, payload.user)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'inside_sales_owner',
          data: payload.user
        } as ISetQuoteFieldStateMutationPayload<'inside_sales_owner'>);
      });
  },

  setQuoteRegion(
    context: QuoteContext,
    payload: IQuoteSetRegionActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteRegion(payload.id, payload.region)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'region',
          data: payload.region
        } as ISetQuoteFieldStateMutationPayload<'region'>);
      });
  },

  setQuoteAgent(
    context: QuoteContext,
    payload: IQuoteSetUserActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteAgent(payload.id, payload.user)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'agent',
          data: payload.user
        } as ISetQuoteFieldStateMutationPayload<'agent'>);
      });
  },

  setComments(
    context: QuoteContext,
    payload: IQuoteSetCommentsActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuoteComments(payload.id, payload.comments)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'comments',
          data: payload.comments
        } as ISetQuoteFieldStateMutationPayload<'comments'>);
      });
  },

  updateConfigurationItemDiscount(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemSetActionPayload
  ): Promise<IConfigurationItemData> {
    return this.$services.quote.updateConfigurationItemDiscount(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.item
    );
  },

  updateConfigurationItemOrder(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemSetActionPayload
  ): Promise<IConfigurationItemData> {
    return this.$services.quote.updateConfigurationItemOrder(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.item
    );
  },

  updateConfigurationItemQuantity(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemSetActionPayload
  ): Promise<IConfigurationItemData> {
    return this.$services.quote.updateConfigurationItemQuantity(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.item
    );
  },

  updateConfigurationItemUnitPrice(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemSetActionPayload
  ): Promise<IConfigurationItemData> {
    return this.$services.quote.updateConfigurationItemUnitPrice(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.item
    );
  },

  updateConfigurationItemUnitCost(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemSetActionPayload
  ): Promise<IConfigurationItemData> {
    return this.$services.quote.updateConfigurationItemUnitCost(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.item
    );
  },

  fetchQuoteConfigurationItems(
    _context: QuoteContext,
    payload: IQuoteConfigurationItemGetActionPayload
  ): Promise<IConfigurationItemData[]> {
    return this.$services.quote.fetchQuoteConfigurationItems(
      payload.id,
      payload.revision,
      payload.quoteProductCategory,
      payload.language
    );
  },

  applyGlobalDiscountPercentage(
    _context: QuoteContext,
    payload: IQuoteGlobalDiscountActionPayload
  ): Promise<void> {
    return this.$services.quote.applyGlobalDiscountPercentage(
      payload.id,
      payload.revision,
      payload.category,
      payload.global_discount_percentage,
      payload.accessory_discount_percentage,
      payload.configuration
    );
  },

  applyFirstYearSubscriptionDiscountPercentage(
    _context: QuoteContext,
    payload: IQuoteFirstYearDiscountActionPayload
  ): Promise<void> {
    return this.$services.quote.applyFirstYearSubscriptionDiscountPercentage(
      payload.id,
      payload.revision,
      payload.category,
      payload.first_year_subscription_discount_percentage
    );
  },

  importQuoteVentilationXls(
    context: QuoteContext,
    payload: IQuoteImportActionPayload
  ): Promise<void> {
    return this.$services.quote
      .importQuoteVentilationXls(payload.quoteId, payload.file)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  exportQuoteProposal(
    _context: QuoteContext,
    payload: IQuoteExportActionPayload
  ): Promise<void> {
    return this.$services.quote.exportQuoteProposal(
      payload.quoteId,
      payload.language
    );
  },

  exportQuoteOrderReview(
    _context: QuoteContext,
    payload: IQuoteExportActionPayload
  ): Promise<Blob> {
    return this.$services.quote.exportQuoteOrderReview(
      payload.quoteId,
      payload.language
    );
  },

  exportQuoteXML(
    _context: QuoteContext,
    payload: IQuoteExportXMLActionPayload
  ): Promise<void> {
    return this.$services.quote.exportQuoteXML(payload);
  },

  exportProformaInvoice(
    _context: QuoteContext,
    payload: IQuoteExportProformaActionPayload
  ): Promise<void> {
    return this.$services.quote.exportQuoteProformaInvoice(
      payload.quoteId,
      payload.orderNumber,
      payload.poNumber,
      payload.hsCode,
      payload.originCountry,
      payload.deliveryMode,
      payload.exfactoryDate,
      payload.language,
      payload.showHsCode,
      payload.showCoo
    );
  },

  createOrderFromQuote(
    context: QuoteContext,
    payload: IQuoteOrderActionPayload
  ): Promise<void> {
    return this.$services.quote
      .createOrder(payload.id, payload.order, payload.customer)
      .then(({ state }) => {
        context.commit('SET_QUOTE_STATE', { id: payload.id, state: state });
        return state;
      });
  },

  evaluateComputationPrice(
    _context: QuoteContext,
    payload: IQuoteComputationEvaluatePricePayload
  ): Promise<number> {
    return this.$services.quote.evaluateComputationPrice(
      payload.id,
      payload.revision,
      payload.computation
    );
  },

  evaluateComputationDays(
    _context: QuoteContext,
    payload: IQuoteComputationEvaluatePricePayload
  ): Promise<number> {
    return this.$services.quote.evaluateComputationDays(
      payload.id,
      payload.revision,
      payload.computation
    );
  },

  calculateCoreDiscountPercent(
    _context: QuoteContext,
    payload: IQuoteCalculateCoreDiscountPercentPayload
  ): Promise<number> {
    return this.$services.quote
      .calculateCoreDiscountPercent(payload.id, payload.data)
      .then(({ core_discount_percent }) => core_discount_percent);
  },

  applyDiscountPercentages(
    context: QuoteContext,
    payload: IQuoteApplyDiscountPercentagesPayload
  ): Promise<void> {
    return this.$services.quote
      .applyDiscountPercentages(payload.id, payload.data)
      .then((quote: IQuoteData) => {
        const index = context.state.quotes.findIndex(
          (v: IQuoteData) => v.number === quote.number
        );
        if (index === -1) {
          context.commit('ADD_QUOTE', quote);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_QUOTE', quote);
        }
      });
  },

  applySurcharges(
    context: QuoteContext,
    payload: IQuoteApplySurchargesPayload
  ): Promise<void> {
    return this.$services.quote
      .applySurcharges(payload.id, payload.applicationType)
      .then((quote: IQuoteData) => {
        context.commit('UPSERT_QUOTE', quote);
      });
  },

  applySurchargeDiscountPercentage(
    context: QuoteContext,
    payload: IQuoteApplySurchargeDiscountPercentagePayload
  ): Promise<void> {
    return this.$services.quote
      .applySurchargeDiscountPercentage(payload.id, payload.discountPercent)
      .then((quote: IQuoteData) => {
        context.commit('UPSERT_QUOTE', quote);
      });
  },

  evaluateSurcharges(
    _context: QuoteContext,
    payload: number
  ): Promise<{
    total_surcharge_all_items: number;
    total_surcharge_mandatory_items: number;
  }> {
    return this.$services.quote.evaluateSurcharges(payload);
  },

  setCustomerRelationshipOwner(
    context: QuoteContext,
    payload: IQuoteSetCustomerRelationshipOwnerActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setCustomerRelationshipOwner(
        payload.id,
        payload.customer_relationship_owner
      )
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'customer_relationship_owner',
          data: payload.customer_relationship_owner
        } as ISetQuoteFieldStateMutationPayload<'customer_relationship_owner'>);
      });
  },

  setBiddingEnvironment(
    context: QuoteContext,
    payload: IQuoteSetBiddingEnvironmentActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setBiddingEnvironment(payload.id, payload.bidding_environment)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'bidding_environment',
          data: payload.bidding_environment
        } as ISetQuoteFieldStateMutationPayload<'bidding_environment'>);
      });
  },

  setClassification(
    context: QuoteContext,
    payload: IQuoteSetClassificationActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setClassification(payload.id, payload.classification)
      .then(() => {
        context.commit('SET_QUOTE_FIELD', {
          id: payload.id,
          field: 'classification',
          data: payload.classification
        } as ISetQuoteFieldStateMutationPayload<'classification'>);
      });
  },

  setQuoteField<T extends keyof IQuoteData>(
    context: QuoteContext,
    payload: ISetQuoteFieldStateMutationPayload<T>
  ): void {
    context.commit('SET_QUOTE_FIELD', {
      id: payload.id,
      field: payload.field,
      data: payload.data
    } as ISetQuoteFieldStateMutationPayload<T>);
  },

  fetchQuotePackages(
    context: QuoteContext,
    payload?: IQuotePackageParams
  ): Promise<void> {
    return this.$services.quote
      .fetchQuotePackages(payload)
      .then((quotePackages: IQuotePackageData[]) => {
        context.commit('SET_QUOTE_PACKAGES', quotePackages);
      });
  },

  fetchQuotePackage(context: QuoteContext, payload: number): Promise<void> {
    return this.$services.quote
      .fetchQuotePackage(payload)
      .then((quotePackage: IQuotePackageData) => {
        const index = context.state.quotePackages.findIndex(
          _package => _package.id === payload
        );
        if (index === -1) {
          context.commit('ADD_QUOTE_PACKAGE', quotePackage);
        } else {
          context.commit('UPDATE_QUOTE_PACKAGE', quotePackage);
        }
      });
  },

  createQuotePackage(
    context: QuoteContext,
    payload: IQuotePackage
  ): Promise<void> {
    return this.$services.quote
      .createQuotePackage(payload)
      .then((quotePackage: IQuotePackageData) => {
        context.commit('ADD_QUOTE_PACKAGE', quotePackage);
      });
  },

  updateQuotePackage(
    context: QuoteContext,
    payload: IQuotePackage
  ): Promise<void> {
    return this.$services.quote
      .updateQuotePackage(payload)
      .then((quotePackage: IQuotePackageData) => {
        const index = context.state.quotePackages.findIndex(
          (v: IQuotePackageData) => v.id === quotePackage.id
        );
        if (index === -1) {
          context.commit('ADD_QUOTE_PACKAGE', quotePackage);
        } else {
          context.commit('UPDATE_QUOTE_PACKAGE', quotePackage);
        }
      });
  },

  setQuotePackageQuotes(
    context: QuoteContext,
    payload: IQuoteSetQuotePackageQuotesActionPayload
  ): Promise<void> {
    return this.$services.quote
      .setQuotePackageQuotes(payload.id, payload.quotes)
      .then((quotePackage: IQuotePackageData) => {
        const index = context.state.quotePackages.findIndex(
          (v: IQuotePackageData) => v.id === quotePackage.id
        );
        if (index === -1) {
          context.commit('ADD_QUOTE_PACKAGE', quotePackage);
        } else {
          context.commit('UPDATE_QUOTE_PACKAGE', quotePackage);
        }
      });
  },

  //BUG Fix - this is needed to update the quote state package key once a quote package is created
  setQuoteQuotePackage(
    context: QuoteContext,
    payload: IQuotePackageData
  ): void {
    context.commit('SET_QUOTE_QUOTE_PACKAGE', payload);
  },

  createQuoteProjectDocument(
    context: QuoteContext,
    payload: IQuotePackageDocument
  ): Promise<IQuotePackageDocumentData> {
    return this.$services.quote
      .createQuoteProjectDocument(payload)
      .then(quotePackageDocument => {
        context.commit('ADD_QUOTE_PACKAGE_DOCUMENT', {
          packageId: payload.quote_package,
          data: quotePackageDocument
        } as IAddQuotePackageDocumentMutationPayload);

        return quotePackageDocument;
      });
  },

  exportQuotePackage(
    _context: QuoteContext,
    payload: IQuoteExportPackageDocumentPayload
  ): Promise<Blob> {
    return this.$services.quote.exportQuotePackage(
      payload.id,
      payload.language
    );
  }
};
